import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { ScrollPanel } from 'primereact/scrollpanel';
import { Toast } from 'primereact/toast';
import { SubmitHandler, useForm } from 'react-hook-form';
import { markDeletedItems } from 'utils/markDeletedItems';

import { AssetTypeSchema } from '../constants/schemas';
import useAssetTypesService from '../hooks/services/useAssetTypesService';
import { AssetType, TagType } from '../types/interfaces';
import { ObjectForSelection } from '../types/interfaces/ObjectForSelection';
import { AssetsTypeProps } from '../types/props';
import { PickerTexts } from '../utils';

import '../ui/css/EmployeeCard.css';
import ConfirmationDialog from './shared/ConfirmationDialog';
import DeleteConfirmationDialog from './shared/DeleteConfirmationDialog';
import { TagsComponent } from './TagsComponent';

interface RefInterface {
  handleCancel: () => void;
}

const AssetTypeCard = React.forwardRef<RefInterface, AssetsTypeProps>(
  (
    {
      assetsTypeProps,
      onAdd,
      onUpdate,
      onDelete,
      closeDialog,
      isMobile
    }: AssetsTypeProps,
    ref: React.Ref<RefInterface>,
  ) => {
    const {
      register,
      handleSubmit,
      watch,
      setValue,
      reset,
      setError,
      formState: { errors }
    } = useForm({
      resolver: zodResolver(AssetTypeSchema),
      defaultValues: assetsTypeProps
    });
    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [assetParameters, setAssetParameters] = useState<
      ObjectForSelection[]
    >([]);
    const [selectedTypeFilter, setSelectedTypeFilter] = useState<TagType[]>(
      watch('parameters')
    );

    const toast = useRef<Toast>(null);
    const name = watch('name');
    const parameters = watch('parameters');
    const businessProcessId = watch('id');

    const assetTypesService = useAssetTypesService();
    useEffect(() => {
      const serviceResult = assetTypesService.getWithAssetParameter(
        assetsTypeProps.id ? parseInt(assetsTypeProps.id.toString()) : undefined
      );
      serviceResult.result.then((r) => {
        if (r.assetType) reset(r.assetType);
        if (r.assetParameters) setAssetParameters(r.assetParameters);
      });
      return () => {
        if (serviceResult) serviceResult.cancel();
      };
    }, [assetTypesService, assetsTypeProps.id, reset]);

    const unsavedChanges =
      JSON.stringify(assetsTypeProps.parameters) !==
        JSON.stringify(parameters) || assetsTypeProps.name !== name;

    const onSubmit: SubmitHandler<AssetType> = async (data) => {
      const newData = { ...data };
      let result: number | undefined = 0;
      const dataId = data.id;
      const isIdExist = data && dataId !== undefined && dataId !== null;
      if (onUpdate && isIdExist) {
        result = await onUpdate(dataId, newData);
      }
      if (onAdd && !isIdExist) {
        newData.id = 0;
        result = await onAdd(newData);
      }
      if (result === -1) {
        setError('name', {
          type: 'value',
          message:
            'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальное название'
        });
      } else {
        closeDialog && closeDialog();
      }
    };

    const handleCancel = () => {
      if (unsavedChanges) {
        setShowDialog(true);
      }
      if (closeDialog && !unsavedChanges) {
        closeDialog();
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
      // Perform navigation/close the card without saving
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleDeleteConfirmation = () => {
      if (onDelete) {
        onDelete(businessProcessId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    const handleDeleteTaskStatus = (taskId: string | number) => {
      const index = selectedTypeFilter.findIndex(
        (tagComponent) => tagComponent.id === taskId
      );

      if (index !== -1) {
        const updatedTaskStatuses = [
          ...selectedTypeFilter.slice(0, index),
          ...selectedTypeFilter.slice(index + 1)
        ];
        setSelectedTypeFilter(updatedTaskStatuses);
        setValue('parameters', updatedTaskStatuses);
        setSelectedTypeFilter(updatedTaskStatuses);
      }
    };


    return (
      <div className="dialog-tabless-card dialog-tabless-card--fixed-footer assetType-card__roles">
        <Toast ref={toast} />
        <div className=" gap-3 pb-0">
          <Divider />
          <div className="asset-type-card__column">
            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <label htmlFor="name" className="p-0">
                    Название<span className="required">*</span>:
                  </label>
                  <div className="col p-0">
                    <InputText
                      className="user-card__input w-full"
                      {...register('name')}
                    />
                  </div>
                </div>
                <p
                  className={`asset-type-card-errors card-errors ${
                    errors.name ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.name ? errors.name.message : ' '}
                </p>
              </div>
            </div>

            <div className="flex flex-row gap-5">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <MultiSelect
                    value={parameters}
                    options={assetParameters}
                    onChange={(e) => {
                      const selectedValues = e.value as ObjectForSelection[];
                      setValue('parameters', selectedValues);
                      setSelectedTypeFilter(selectedValues);
                    }}
                    optionLabel="label"
                    // placeholder="Выбор характеристик"
                    placeholder={PickerTexts.characteristics(parameters.length)}
                    fixedPlaceholder
                    filter
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-5 dialog-card__row tag-component">
              <div className=" w-full">
                <div className="flex flex flex-row justify-content-between align-items-center gap-3 w-full">
                  <Card
                    style={{ width: '100%', height: '185px', color: '#6c757d' }}
                  >
                    <ScrollPanel style={{ width: '100%', height: '150px' }}>
                      {selectedTypeFilter.length === 0 && (
                        <p>Характеристики не выбраны</p>
                      )}
                      <TagsComponent
                        initialTagsItems={markDeletedItems<TagType>(selectedTypeFilter, assetParameters)}
                        onDelete={handleDeleteTaskStatus} // Pass the onDelete function
                      />
                    </ScrollPanel>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className={`${isMobile ? 'default-card__buttons--mobile' : ''} default-card__buttons`}>
          <div 
            className="flex gap-3" 
            style={{
              flexDirection: isMobile ? 'column' : 'row',
            }}
          >
            <Button
              label="Сохранить"
              icon={isMobile ? undefined : 'pi pi-check'}
              onClick={handleSubmit(onSubmit)}
            />
            <Button
              label="Удалить"
              icon={isMobile ? undefined : 'pi pi-trash'}
              onClick={handleDelete}
              className="p-button-danger"
            />
            <Button
              label="Отмена"
              icon={isMobile ? undefined : 'pi pi-times'}
              onClick={handleCancel}
              className="p-button-secondary"
            />
          </div>
        </div>

        <ConfirmationDialog
          visible={showDialog}
          onCancel={handleConfirmationCancel}
          onContinue={handleConfirmationContinue}
        />

        <DeleteConfirmationDialog
          visible={showDeleteDialog}
          onHide={handleDeleteCancellation}
          onConfirm={handleDeleteConfirmation}
          entityType="тип объекта"
          entityName={name}
        />
      </div>
    );
  }
);

AssetTypeCard.displayName = 'AssetTypeCard';

export default AssetTypeCard;
